/**
 * Cookie Consent Module
 * 
 * Manages user consent for different cookie categories.
 * - Performance: GA4 loaded dynamically, consent mode controls analytics_storage
 * - Functional: Language preference in localStorage, removed when consent withdrawn
 * - Targeting: Consent mode controls ad_storage (currently no ad cookies in use)
 */

// GA4 Measurement ID
const GA4_MEASUREMENT_ID = 'G-C492YM67Q3';

// Version for detecting program updates
const CURRENT_VERSION = 1;

// Cookie storage key
const STORAGE_KEY = 'cookie-consent';

// Track if GA4 script has been loaded
let ga4ScriptLoaded = false;

// Cookie categories
export const COOKIE_CATEGORIES = {
  STRICTLY_NECESSARY: 'strictly_necessary',
  FUNCTIONAL: 'functional',
  PERFORMANCE: 'performance',
  TARGETING: 'targeting'
};

// Default consent state
const defaultConsent = () => ({
  strictly_necessary: true,
  functional: false,
  performance: false,
  targeting: false
});

/**
 * Dynamically load GA4 script only when user consents
 */
function loadGA4Script() {
  if (ga4ScriptLoaded) return;
  ga4ScriptLoaded = true;

  // Initialize dataLayer and gtag before loading script
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  // Set consent defaults FIRST (before script loads)
  gtag('consent', 'default', {
    'analytics_storage': 'denied',
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied'
  });

  // Load the GA4 script dynamically
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  script.onload = () => {
    gtag('js', new Date());
    gtag('config', GA4_MEASUREMENT_ID, {
      'cookie_flags': 'SameSite=Lax;Secure'
    });
  };
  document.head.appendChild(script);
}

/**
 * Update GA4 consent mode based on all user preferences.
 * This is the official Google-recommended approach.
 * Removing the script tag does NOT stop tracking; consent mode must be updated.
 * @param {object} preferences - user cookie preferences
 */
function updateGA4Consent(preferences) {
  if (!window.gtag) return;

  window.gtag('consent', 'update', {
    'analytics_storage': preferences.performance ? 'granted' : 'denied',
    'ad_storage': preferences.targeting ? 'granted' : 'denied',
    'ad_user_data': preferences.targeting ? 'granted' : 'denied',
    'ad_personalization': preferences.targeting ? 'granted' : 'denied'
  });

  // Clear GA cookies if performance denied
  if (!preferences.performance) {
    clearGACookies();
  }
}

/**
 * Clear GA cookies to stop tracking when consent is withdrawn
 */
function clearGACookies() {
  const gaCookiePatterns = [/^_ga/, /^_gid/, /^_gat/, /^_ga_/];
  const cookies = document.cookie.split(';');

  cookies.forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    const shouldDelete = gaCookiePatterns.some(pattern => pattern.test(name));
    if (shouldDelete) {
      // Delete from all possible domain scopes
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax; Secure`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${location.hostname}; SameSite=Lax; Secure`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${location.hostname}; SameSite=Lax; Secure`;
      // Also try with port (for localhost)
      const domainParts = location.hostname.split('.');
      for (let i = 1; i <= domainParts.length; i++) {
        const domain = '.' + domainParts.slice(i - 1).join('.');
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}; SameSite=Lax; Secure`;
      }
    }
  });
}

export default {
  namespaced: true,

  state: () => ({
    preferences: defaultConsent(),
    hasConsented: false,
    version: CURRENT_VERSION,
    isModalVisible: false,
    isSettingsExpanded: false
  }),

  getters: {
    isAllAccepted(state) {
      return state.hasConsented && 
             state.preferences.functional && 
             state.preferences.performance && 
             state.preferences.targeting;
    },
    shouldShowModal(state) {
      return !state.hasConsented;
    },
    isPerformanceAccepted(state) {
      return state.preferences.performance;
    },
    isTargetingAccepted(state) {
      return state.preferences.targeting;
    }
  },

  mutations: {
    SET_PREFERENCES(state, preferences) {
      state.preferences = { ...state.preferences, ...preferences };
    },
    SET_HAS_CONSENTED(state, value) {
      state.hasConsented = value;
    },
    SET_MODAL_VISIBLE(state, visible) {
      state.isModalVisible = visible;
    },
    SET_SETTINGS_EXPANDED(state, expanded) {
      state.isSettingsExpanded = expanded;
    },
    LOAD_SAVED_CONSENT(state) {
      try {
        // Check for old localStorage key for migration
        const oldAccepted = localStorage.getItem('accepted-cookies');
        if (oldAccepted === 'true') {
          state.preferences = defaultConsent();
          state.hasConsented = true;
          state.version = CURRENT_VERSION;
          const data = { ...state.preferences, hasConsented: true, version: CURRENT_VERSION };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
          localStorage.removeItem('accepted-cookies');
          return;
        }
        
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const parsed = JSON.parse(saved);
          state.preferences = {
            strictly_necessary: parsed.strictly_necessary ?? true,
            functional: parsed.functional ?? false,
            performance: parsed.performance ?? false,
            targeting: parsed.targeting ?? false
          };
          state.hasConsented = parsed.hasConsented ?? false;
          state.version = parsed.version ?? CURRENT_VERSION;
        }
      } catch (e) {
        console.warn('Failed to load cookie consent from localStorage:', e);
      }
    },
    RESET_CONSENT(state) {
      state.preferences = defaultConsent();
      state.hasConsented = false;
      state.isModalVisible = false;
      state.isSettingsExpanded = false;
    }
  },

  actions: {
    /**
     * Initialize module - load saved consent, load GA4 if already consented
     */
    init({ commit, state }) {
      commit('LOAD_SAVED_CONSENT');
      
      // If user already consented, load GA4 and sync all consent states
      if (state.hasConsented) {
        loadGA4Script();
        updateGA4Consent(state.preferences);
      }
      
      // Show modal if user hasn't consented
      if (!state.hasConsented) {
        commit('SET_MODAL_VISIBLE', true);
      }
    },

    acceptAll({ commit, dispatch }) {
      const preferences = { ...defaultConsent(), functional: true, performance: true, targeting: true };
      commit('SET_PREFERENCES', preferences);
      commit('SET_HAS_CONSENTED', true);
      commit('SET_MODAL_VISIBLE', false);
      commit('SET_SETTINGS_EXPANDED', false);
      dispatch('saveConsent');
      loadGA4Script();
      updateGA4Consent(preferences);
    },

    acceptNecessaryOnly({ commit, dispatch }) {
      const preferences = defaultConsent();
      commit('SET_PREFERENCES', preferences);
      commit('SET_HAS_CONSENTED', true);
      commit('SET_MODAL_VISIBLE', false);
      commit('SET_SETTINGS_EXPANDED', false);
      dispatch('saveConsent');
      loadGA4Script();
      updateGA4Consent(preferences);
    },

    updatePreferences({ commit, dispatch }, preferences) {
      const finalPreferences = { ...preferences, strictly_necessary: true };
      commit('SET_PREFERENCES', finalPreferences);
      commit('SET_HAS_CONSENTED', true);
      commit('SET_MODAL_VISIBLE', false);
      commit('SET_SETTINGS_EXPANDED', false);
      dispatch('saveConsent');
      loadGA4Script();
      updateGA4Consent(finalPreferences);
    },

    showModal({ commit }) {
      commit('SET_MODAL_VISIBLE', true);
    },

    hideModal({ commit }) {
      commit('SET_MODAL_VISIBLE', false);
    },

    toggleSettings({ commit, state }) {
      commit('SET_SETTINGS_EXPANDED', !state.isSettingsExpanded);
    },

    /**
     * Open modal directly to settings panel (from Footer "Cookie Settings" link).
     * Shows modal + expands settings in one action.
     */
    openSettingsFromFooter({ commit, state }) {
      commit('SET_MODAL_VISIBLE', true);
      if (!state.isSettingsExpanded) {
        commit('SET_SETTINGS_EXPANDED', true);
      }
    },

    /**
     * Collapse settings panel back to the Cookie Settings layer.
     * Used by the "Back to Cookie Settings" button in manage settings.
     */
    collapseToCookieSettings({ commit }) {
      commit('SET_SETTINGS_EXPANDED', false);
    },

    /**
     * Reset all consent - for testing or privacy compliance.
     * Clears localStorage, resets state, shows modal, updates GA4.
     */
    resetConsent({ commit }) {
      commit('RESET_CONSENT');
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('accepted-cookies');
      commit('SET_MODAL_VISIBLE', true);
      updateGA4Consent(defaultConsent());
    },

    saveConsent({ state }) {
      try {
        const data = {
          ...state.preferences,
          hasConsented: state.hasConsented,
          version: CURRENT_VERSION
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        console.warn('Failed to save cookie consent:', e);
      }
    },

    checkVersion({ state, commit }) {
      if (state.hasConsented && state.version < CURRENT_VERSION) {
        commit('SET_HAS_CONSENTED', false);
        commit('SET_MODAL_VISIBLE', true);
      }
    }
  }
};

export { GA4_MEASUREMENT_ID, CURRENT_VERSION, STORAGE_KEY };